import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// Same cProxy URL requested from multiple places (re-renders, multiple components showing the
// same generated media) reuses one blob URL instead of re-fetching. Not ref-counted/revoked -
// per-session media counts are small enough that this is an acceptable simplification (see
// TASKS.md), not something to over-engineer.
const blobUrlCache = new Map<string, Promise<string>>();

const getBlobUrl = (url: string): Promise<string> => {
    let cached = blobUrlCache.get(url);
    if (!cached) {
        cached = fetchWithAuth(url)
            .then(res => {
                if (!res.ok) throw new Error(`Failed to load media (HTTP ${res.status})`);
                return res.blob();
            })
            .then(blob => URL.createObjectURL(blob));
        blobUrlCache.set(url, cached);
        cached.catch(() => blobUrlCache.delete(url));
    }
    return cached;
};

/**
 * Resolves a cProxy-hosted media URL (behind Bearer-auth, e.g. /api/cproxy/v1/downloads/*) to a
 * local blob: URL that <img src>/<video src>/ReactPlayer url can use directly - a plain src/url
 * can't carry an Authorization header. Required since UniversalProxy's require_user_auth
 * fail-closed fix (2026-07-14) broke plain <img>/<video> tags pointed at cProxy downloads - see
 * TASKS.md, "cProxy downloads require auth".
 */
export const useAuthedMediaUrl = (url: string | null | undefined): string | null => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!url) {
            setBlobUrl(null);
            return;
        }
        let cancelled = false;
        setBlobUrl(null);
        getBlobUrl(url).then(resolved => { if (!cancelled) setBlobUrl(resolved); }).catch(() => {});
        return () => { cancelled = true; };
    }, [url]);

    return blobUrl;
};
